import { h, Fragment } from "preact";
import Router, { RoutableProps } from "preact-router";
import { useCallback, useEffect, useMemo, useRef, useState } from "preact/hooks";
import { resolvePath } from "../../../../api";
import { generate, getLabelIds, labelPreviewUrl, labelsMetadata } from "../../../../api/label-creator";
import {
    createGroupLabel,
    createTemplateLabel,
    deleteGroupLabel,
    deleteLabel,
    deleteTemplateLabel,
    editGroupLabel,
    editTemplateLabel,
    getChildLabels,
    getGroupLabel,
    getGroupLabels,
    getLabels,
    getTemplateLabel,
    Label,
    ProductType,
} from "../../../../api/product-api";
import { baseUrl, route } from "../../../../App";
import { Snackbar, SnackbarShowCommand } from "../../../../components/Snackbar";
import { TemplatePrint, parseTapeType } from "../../../../components/TemplatePrint";
import { useCommand } from "../../../../hooks";
import {
    generateExampleFieldsForLabel,
    generateLabelFields,
    getExampleSerialNumber,
    parseToDateAndFormatIfExists,
} from "../../../../utils";
import { predefinedMetadataKeys, TemplateMetadataDisplay } from "../../../../components/TemplateMetadataDisplay";
import "./styles.scss";
import { LabelMetadataEditorHelp } from "../../LabelMetadataEditorHelp";
import { LabelMetadataFieldEdit } from "../../LabelMetadataFieldEdit";
import { LabelSVGInteractive } from "../../LabelSVGInteractive";
import { SimpleList } from "../../SimpleList";
import { LabelMetadataElementList } from "../../LabelMetadataElementList";
import { FileInput } from "../../../../components/FileInput";
import { CompactTableItemSelector } from "../../../../components/CompactTableItemSelector";
import { twMerge } from "tailwind-merge";
import { cn } from "../../../../helpers/cn";
import { countries } from "../../../../data/countries";
import { distinct } from "../../../../helpers/distinct";

function TestLabelPreview({
    file,
    label,
    labelMetadata,
}: {
    file: File | null;
    label: Label;
    labelMetadata: { [key: string]: string };
}) {
    const [src, setSrc] = useState("");

    useEffect(() => {
        if (!file || !label || !labelMetadata) {
            setSrc("");
            return;
        }

        generate(
            file,
            generateExampleFieldsForLabel({ ...labelMetadata, ...label.metadata?.metadata }),
            label.metadata?.metadata,
        ).then(v => setSrc(v));
    }, [file, label, labelMetadata]);

    return !src ? null : <img style="max-width: 800px; max-height: 500px" src={src} />;
}

export function LabelGroupEditPage(props: RoutableProps & { id?: string; cloneId?: string }) {
    const [group, setGroup] = useState(null as null | Label);
    const [innerLabels, setInnerLabels] = useState(
        [] as { label: Label; productType?: ProductType | null; order: number }[],
    );
    const [labelIds, setLabelIds] = useState([] as string[]);
    const [labelMetadata, setLabelMetadata] = useState({} as { [key: string]: string });
    const [newEntryName, setNewEntryName] = useState("");
    const [labelSvg, setLabelSvg] = useState("");
    const [labelSvgOpacity, setLabelSvgOpacity] = useState(0);
    const showSnackbarCommand = useCommand<SnackbarShowCommand>();
    const templateMetadataDisplayClear = useCommand();
    const templateMetadataDisplaySetMetadata = useCommand<[metadata: { [key: string]: string }]>();
    const [childLabels, setChildLabels] = useState(null as null | Label[]);
    const [labels, setLabels] = useState(null as null | Label[]);
    const [label, setLabel] = useState(null as null | Label);
    const [groupType, setGroupType] = useState("multipart" as "multipart" | "set");

    useEffect(() => {
        if (props.id) {
            getGroupLabel(+props.id).then(async label => {
                setGroup(label);
                setGroupType(label.metadata?.metadata?.groupType === "set" ? "set" : "multipart");
                setInnerLabels(
                    (label.innerLabels ?? []).map(innerLabel => ({
                        ...innerLabel,
                        productType:
                            innerLabel.productType ??
                            innerLabel.label.productTypeRevisionLabels?.[0]?.productTypeRevision?.productType ??
                            null,
                    })),
                );
            });
        } else if (props.cloneId) {
            getGroupLabel(+props.cloneId).then(async label => {
                delete (label as any).id;
                delete (label as any).createdAt;
                delete (label as any).creator;
                setGroup(label);
                setGroupType(label.metadata?.metadata?.groupType === "set" ? "set" : "multipart");
                setInnerLabels(
                    (label.innerLabels ?? []).map(innerLabel => ({
                        ...innerLabel,
                        productType:
                            innerLabel.productType ??
                            innerLabel.label.productTypeRevisionLabels?.[0]?.productTypeRevision?.productType ??
                            null,
                    })),
                );
            });
        } else {
            setGroup({
                metadata: {
                    metadata: {},
                },
            } as Label);
        }

        getLabels().then(async labels => {
            const groups = await getGroupLabels();
            setLabels(labels.concat(groups.filter(v => v.metadata?.metadata?.groupType === "multipart")));
        });
    }, [props.id, props.cloneId]);

    useEffect(() => {
        if (!props.id) {
            setChildLabels([]);
            return;
        }

        getChildLabels(+props.id).then(v => setChildLabels(v));
    }, [props.id]);

    useEffect(() => {
        if (!group?.path) {
            setLabelIds([]);
            setLabelMetadata({});
            return;
        }

        getLabelIds(group.path)
            .then(async label => {
                setLabelIds(label);
            })
            .catch(() => {
                showSnackbarCommand.run("Nie udało się pobrać danych naklejki", "error", "error");
                setLabelIds([]);
            });

        labelsMetadata(group.path)
            .then(async label => {
                setLabelMetadata(label);
            })
            .catch(() => {
                showSnackbarCommand.run("Nie udało się pobrać danych naklejki", "error", "error");
                setLabelIds([]);
            });

        fetch(labelPreviewUrl(group.path)).then(v =>
            v.text().then(t => {
                setLabelSvg(t);

                const length = parseFloat((/viewBox="\d+ \d+ (\d+) \d+"/i.exec(t) ?? ["", ""])[1]);

                if (group.metadata?.metadata) {
                    group.metadata.metadata["label-length"] = (length / 14 + 2).toPrecision(2) + "mm";
                }
            }),
        );
    }, [group?.path]);

    useEffect(() => {
        if (!label) {
            setLabelSvg("");
            return;
        }

        fetch(generate(label.path, {}, label.metadata?.metadata, true, true)).then(v =>
            v.text().then(t => {
                setLabelSvg(t);
            }),
        );
    }, [label]);

    if (!group) {
        return (
            <div style="display: flex; flex-direction: column; justify-content: center; align-items: center; width: 100%; height: 100%">
                <div>Pobieranie danych...</div>
                <div class="loader"></div>
            </div>
        );
    }

    return (
        <div class="label-edit-page">
            <Snackbar doShow={showSnackbarCommand} />
            <div class="order-items paper" style="margin-top: 10px;">
                <h1>
                    {group.id
                        ? groupType === "set"
                            ? "Zestaw nalejek: " + group.name
                            : "Naklejka wieloczęściowa: " + group.name
                        : groupType === "set"
                          ? "Nowy zestaw naklejek"
                          : "Nowa naklejka wieloczęściowa"}
                </h1>
                <div style="display: flex">
                    <div style="flex: 1 0">
                        <div>
                            <label>
                                <span style="width: 120px; text-align: right">Rodzaj:</span>
                                <select
                                    style="margin-left: 5px"
                                    value={groupType}
                                    onChange={e => setGroupType(e.currentTarget.value as "multipart" | "set")}
                                >
                                    <option value="multipart">Naklejka wieloczęściowa</option>
                                    <option value="set">Zestaw naklejek</option>
                                </select>
                            </label>
                        </div>
                        <div class="mt-2.5">
                            <label>
                                <span style="width: 120px; text-align: right">
                                    Nazwa {groupType === "set" ? "zestawu" : "naklejki"}:
                                </span>
                                <input
                                    style="margin-left: 5px"
                                    type="text"
                                    value={group.name}
                                    onChange={e => {
                                        setGroup({
                                            ...group,
                                            name: e.currentTarget.value,
                                        });
                                    }}
                                />
                            </label>
                        </div>
                        <div style="margin-top: 10px;">
                            <label>
                                <span style="width: 120px; text-align: right">Rewizja:</span>
                                <input
                                    style="margin-left: 5px"
                                    type="text"
                                    value={group.revision}
                                    onChange={e => {
                                        setGroup({
                                            ...group,
                                            revision: e.currentTarget.value,
                                        });
                                    }}
                                />
                            </label>
                        </div>
                        <div style="margin-top: 10px;">
                            <label>
                                <span style="width: 120px; text-align: right;">Kraj:</span>{" "}
                                <select
                                    style="margin-left: 5px;"
                                    value={group.metadata?.metadata?.country ?? ""}
                                    onChange={e => {
                                        setGroup({
                                            ...group,
                                            metadata: {
                                                ...group.metadata,
                                                metadata: {
                                                    ...group.metadata?.metadata,
                                                    country: e.currentTarget.value,
                                                },
                                            },
                                        });
                                    }}
                                >
                                    <option value={""}>Standardowe</option>
                                    {Object.entries(countries).map(([key, value]) => (
                                        <option value={key}>
                                            {value} ({key})
                                        </option>
                                    ))}
                                </select>
                            </label>
                        </div>
                    </div>
                </div>
                <div class="mt-5">
                    <h2>Dodaj naklejki do zestawu</h2>
                    <CompactTableItemSelector
                        class="max-h-96"
                        items={
                            labels?.filter(
                                v =>
                                    !innerLabels.find(inner => inner.label.id === v.id) &&
                                    (v.productTypeRevisionLabels?.length ?? 0) > 0,
                            ) ?? []
                        }
                        selected={label}
                        properties={[
                            { name: "name", type: "text", title: "Nazwa", search: true },
                            {
                                name: "productType",
                                type: "text",
                                title: "Typ produktu",
                                search: true,
                                format: v =>
                                    v
                                        .split("\n")
                                        .map((v, i, arr) => <div class={i === arr.length - 1 ? "" : "mb-1"}>{v}</div>),
                                valueProvider: item =>
                                    distinct(
                                        item.productTypeRevisionLabels
                                            ?.map(v => v.productTypeRevision?.productType?.name)
                                            .filter(Boolean) ?? [],
                                    ).join("\n") ?? "",
                            },
                            {
                                name: "brand",
                                type: "text",
                                title: "Brand produktu",
                                search: true,
                                format: v =>
                                    v
                                        .split("\n")
                                        .map((v, i, arr) => <div class={i === arr.length - 1 ? "" : "mb-1"}>{v}</div>),
                                valueProvider: item =>
                                    distinct(
                                        item.productTypeRevisionLabels
                                            ?.map(v => v.productTypeRevision?.productType?.brand)
                                            .filter(Boolean) ?? [],
                                    ).join("\n") ?? "",
                            },
                            {
                                name: "tapeWidth",
                                type: "text",
                                title: "Szerokość taśmy",
                                search: true,
                                valueProvider: item => item.metadata?.metadata["tape-width"] ?? "",
                            },
                            { name: "path", type: "text", title: "Ścieżka", search: true },
                            { name: "revision", type: "text", title: "Rewizja", search: true },
                            {
                                name: "country",
                                type: "text",
                                title: "Kraj",
                                search: true,
                                valueProvider: item =>
                                    `${
                                        item.metadata?.metadata?.country
                                            ? countries[item.metadata.metadata.country as keyof typeof countries]
                                            : "Standardowe"
                                    } ${
                                        item.metadata?.metadata?.country ? ` (${item.metadata.metadata.country})` : ""
                                    }`,
                            },
                            { name: "creator.email", type: "text", title: "Rejestrujący", search: true },
                            { name: "createdAt", type: "date", title: "Data rejestracji", search: true },
                        ]}
                        onSelect={v => {
                            setLabel(v);
                        }}
                    />
                    <button
                        type="button"
                        class={cn("mt-2", !label ? "disabled" : "")}
                        onClick={() => {
                            if (label) {
                                setInnerLabels(prev => [
                                    ...prev,
                                    {
                                        label,
                                        productType:
                                            label.productTypeRevisionLabels?.[0]?.productTypeRevision?.productType,
                                        order: prev.length,
                                    },
                                ]);
                                setLabel(null);
                            }
                        }}
                    >
                        {groupType === "multipart" ? "Dodaj do wieloczęściowej naklejki" : "Dodaj do zestawu"}
                    </button>
                    <div class="mt-2 flex flex-col gap-2">
                        <div>Podgląd naklejki</div>
                        <LabelSVGInteractive
                            maxWidth={500}
                            maxHeight={300}
                            svg={labelSvg}
                            onReload={() => setLabelSvgOpacity(0)}
                            onLoad={() => setLabelSvgOpacity(1)}
                        />
                    </div>
                </div>
                <div class="mt-5">
                    <h2>{groupType === "multipart" ? "Części naklejki" : "Naklejki w zestawie"}</h2>
                    <div>
                        {innerLabels.map((v, index) => (
                            <InnerLabel
                                key={v.label.id}
                                index={index}
                                label={v.label}
                                innerLabels={innerLabels}
                                setInnerLabels={setInnerLabels}
                                groupType={groupType}
                                productType={v.productType}
                            />
                        ))}
                    </div>
                </div>
                {group.id ? (
                    <div class="mt-10 flex flex-row">
                        <span
                            class={!childLabels || childLabels.length > 0 ? "tooltip" : ""}
                            data-tooltip="Nie można edytować szablonu jeśli zostały utworzone za pomocą niego naklejki"
                        >
                            <button
                                type="button"
                                class={!childLabels || childLabels.length > 0 ? "disabled" : ""}
                                onClick={async () => {
                                    try {
                                        await editGroupLabel({
                                            ...group,
                                            metadata: {
                                                metadata: {
                                                    ...group.metadata?.metadata,
                                                    groupType: groupType,
                                                },
                                            },
                                            contents: innerLabels.map(v => ({
                                                labelId: v.label.id,
                                                productTypeId: groupType === "multipart" ? null : v.productType?.id,
                                            })),
                                        });
                                    } catch (ex) {
                                        console.error(ex);
                                        showSnackbarCommand.run(
                                            "Wystąpił błąd podczas edytowania zestawu naklejek",
                                            "error",
                                            "error",
                                        );
                                        return;
                                    }

                                    route("/label/group", true);
                                }}
                            >
                                Zastosuj
                            </button>
                        </span>
                        <span class="grow"></span>
                        <span
                            class={
                                (group.productTypeRevisionLabels?.filter(v => v.productTypeRevision) ?? []).length > 0
                                    ? "tooltip"
                                    : ""
                            }
                            data-tooltip="Nie można usunąć naklejki, która została przypisana do rewizji produktu"
                        >
                            <button
                                type="button"
                                class={
                                    "error" +
                                    ((group.productTypeRevisionLabels?.filter(v => v.productTypeRevision) ?? [])
                                        .length > 0
                                        ? " disabled"
                                        : "")
                                }
                                onClick={async () => {
                                    try {
                                        await deleteGroupLabel(group.id);
                                    } catch (ex) {
                                        console.error(ex);
                                        showSnackbarCommand.run(
                                            "Wystąpił błąd podczas usuwania zestawu naklejek",
                                            "error",
                                            "error",
                                        );
                                    }

                                    route("/label/group", true);
                                }}
                            >
                                Usuń
                            </button>
                        </span>
                    </div>
                ) : (
                    <div style="margin-top: 40px;">
                        <button
                            type="button"
                            onClick={async () => {
                                try {
                                    await createGroupLabel({
                                        ...group,
                                        metadata: {
                                            metadata: {
                                                ...group.metadata?.metadata,
                                                groupType: groupType,
                                            },
                                        },
                                        contents: innerLabels.map(v => ({
                                            labelId: v.label.id,
                                            productTypeId: groupType === "multipart" ? null : v.productType?.id,
                                        })),
                                    });
                                } catch (ex) {
                                    console.error(ex);
                                    showSnackbarCommand.run(
                                        "Wystąpił błąd podczas tworzenia zestawu naklejek",
                                        "error",
                                        "error",
                                    );
                                    return;
                                }

                                route("/label/group", true);
                            }}
                        >
                            Utwórz
                        </button>
                    </div>
                )}
            </div>
            {group ? (
                <div class="paper" style="padding: 10px; margin: 0px 10px; margin-bottom: 10px;">
                    <h1>Rewizje produktów do których przypisany jest zestaw</h1>
                    <div>
                        <table class="list selectable">
                            <thead>
                                <th>Nazwa</th>
                                <th>Brand</th>
                                <th>Wersja</th>
                                <th>Rejestrujący</th>
                                <th>Data rejestracji</th>
                            </thead>
                            <tbody>
                                {group.productTypeRevisionLabels
                                    ?.filter(v => v.productTypeRevision)
                                    .map(v => (
                                        <tr
                                            class="item"
                                            onClick={() => route("/product-type-revision/" + v.productTypeRevision.id)}
                                        >
                                            <td>{v.productTypeRevision.productType!.name}</td>
                                            <td>{v.productTypeRevision.productType!.brand}</td>
                                            <td>{v.productTypeRevision.name}</td>
                                            <td>{v.productTypeRevision.creator.email}</td>
                                            <td>{parseToDateAndFormatIfExists(v.productTypeRevision.createdAt)}</td>
                                        </tr>
                                    ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            ) : null}
        </div>
    );
}

function InnerLabel({
    index,
    label,
    innerLabels,
    setInnerLabels,
    groupType,
    productType,
}: {
    index: number;
    label: Label;
    innerLabels: { label: Label; productType?: ProductType | null; order: number }[];
    setInnerLabels: (
        labels:
            | { label: Label; productType?: ProductType | null; order: number }[]
            | ((
                  prev: { label: Label; productType?: ProductType | null; order: number }[],
              ) => { label: Label; productType?: ProductType | null; order: number }[]),
    ) => void;
    groupType: "multipart" | "set";
    productType?: ProductType | null;
}) {
    return (
        <div style="margin-top: 10px;">
            <table class="list" style="height: 1px">
                <thead>
                    <th class="px-2">Lp.</th>
                    <th>Nazwa</th>
                    <th>Szerokość taśmy</th>
                    <th>Ścieżka</th>
                    <th>Rewizja</th>
                    <th>Rejestrujący</th>
                    <th>Data rejestracji</th>
                    <th>Akcje</th>
                </thead>
                <tbody>
                    <tr>
                        <td>{index + 1}</td>
                        <td>{label.name}</td>
                        <td>{label.metadata?.metadata["tape-width"]}</td>
                        <td>{label.path}</td>
                        <td>{label.revision}</td>
                        <td>{label.creator.email}</td>
                        <td>{parseToDateAndFormatIfExists(label.createdAt)}</td>
                        <td class="h-full whitespace-nowrap w-[1%]">
                            <div class="flex gap-4 items-center justify-center">
                                <div class="flex gap-2">
                                    <button
                                        class={cn("icon !size-6", index === 0 ? "disabled" : "")}
                                        disabled={index === 0}
                                        onClick={async e => {
                                            e.stopPropagation();
                                            e.preventDefault();
                                            setInnerLabels(prev => {
                                                const index = prev.findIndex(inner => inner.label.id === label.id);
                                                if (index <= 0) return prev;
                                                // Use array destructuring for swap
                                                const newInner = [...prev];
                                                [newInner[index - 1], newInner[index]] = [
                                                    newInner[index],
                                                    newInner[index - 1],
                                                ];
                                                // Update order values
                                                newInner[index - 1].order = index - 1;
                                                newInner[index].order = index;
                                                return newInner;
                                            });
                                        }}
                                    >
                                        arrow_drop_up
                                    </button>
                                    <button
                                        class={cn("icon !size-6", index === innerLabels.length - 1 ? "disabled" : "")}
                                        disabled={index === innerLabels.length - 1}
                                        onClick={async e => {
                                            e.stopPropagation();
                                            e.preventDefault();
                                            setInnerLabels(prev => {
                                                const index = prev.findIndex(inner => inner.label.id === label.id);
                                                if (index === -1 || index >= prev.length - 1) return prev;
                                                // Use array destructuring for swap
                                                const newInner = [...prev];
                                                [newInner[index + 1], newInner[index]] = [
                                                    newInner[index],
                                                    newInner[index + 1],
                                                ];
                                                // Update order values
                                                newInner[index + 1].order = index + 1;
                                                newInner[index].order = index;
                                                return newInner;
                                            });
                                        }}
                                    >
                                        arrow_drop_down
                                    </button>
                                </div>
                                <button
                                    class="error"
                                    onClick={async e => {
                                        e.stopPropagation();
                                        e.preventDefault();
                                        setInnerLabels(
                                            prev => prev?.filter(inner => inner.label.id !== label.id) ?? null,
                                        );
                                    }}
                                >
                                    Usuń
                                </button>
                            </div>
                        </td>
                    </tr>
                </tbody>
            </table>
            <div style="margin-top: 10px;">
                {groupType === "set" && (
                    <label class="mb-2.5 flex flex-col gap-1 !items-start">
                        <span>Rodzaj produktu przypisany do naklejki w zestawie:</span>
                        <select
                            value={productType?.id}
                            onChange={e => {
                                const selected = label.productTypeRevisionLabels?.find(
                                    v => v.productTypeRevision.productType?.id === +e.currentTarget.value,
                                );

                                setInnerLabels(prev =>
                                    prev.map(inner =>
                                        inner.label.id === label.id
                                            ? {
                                                  ...inner,
                                                  productType: selected?.productTypeRevision.productType ?? null,
                                              }
                                            : inner,
                                    ),
                                );
                            }}
                        >
                            {label.productTypeRevisionLabels?.map(v => (
                                <option value={v.productTypeRevision.productType?.id}>
                                    ({v.productTypeRevision.productType?.name}){" "}
                                    {v.productTypeRevision.productType?.brand}
                                </option>
                            ))}
                        </select>
                    </label>
                )}
                <img
                    src={generate(
                        label.path,
                        {
                            ...generateExampleFieldsForLabel(label.metadata?.metadata ?? {}),
                            ...generateLabelFields({ revision: productType?.currentProductTypeRevision }),
                        },
                        label.metadata?.metadata,
                    )}
                />
            </div>
        </div>
    );
}
